// import dayjs from 'dayjs'
import expirations from '@packages/cache/expirations'

import { PRIORITY, AGENT_CODE_CLEAN_TYPE } from '@layers/web/constants/api-fetch.ts'
import type {
  CurrentAgentCallsResponse,
  FetchStartCalendarResponse,
  PageBannerBackground,
  ResellerResponse,
  RootStoreState,
  SectionCategory,
  TopNavBarItem,
  TripTypesSideBar,
  WeatherData,
  AgentCookie,
  RolfsEvent,
} from '@layers/web/types/root.ts'

export const useRootStore = defineStore('root', {
  state: (): RootStoreState => ({
    tripTypesSidebar: {},
    topNavbar: [],
    footerLinks: [],
    startCalendar: [],
    startCategories: [],
    charterAirports: [],
    charterDestinations: [],
    charterAirportNames: {},
    pageBannerTitle: '',
    pageBannerTitleI18n: '',
    pageBannerTitleDescription: '',
    pageBannerTitleDescriptionI18n: '',
    pageBannerSubtitle: '',
    pageBannerSubtitleI18n: '',
    pageBannerSubtitleColor: '',
    pageBannerSubtitleWeight: '',
    pageBannerType: '',
    pageMobileBannerType: '',
    pageBannerLink: '',
    pageBannerLinkButton: '',
    pageBannerLinkText: '',
    pageBannerLinkTextI18n: '',
    pageBannerSize: '',
    pageBannerBackground: null,
    pageBannerStars: {},
    pageBannerPrice: null,
    pageBannerSpecial: {},
    bannerPriceWithoutDiscount: null,
    showNavigationArrow: false,
    // Available in: BannerCenteredText
    pageBannerShowSearchBar: false,
    pageNavigationLink: null,
    pagePaddingBottomPx: null,
    pageBannerDays: null,

    pageGradientMask: true,
    pageExtraFooter: null,
    showOfficeTool: false,
    showTranslationTool: false,
    showAgentTools: false,
    cmsTools: false,
    agentCookie: false,
    showAgentCalls: false,
    rolfsCookie: null,
    alert: {},
    localUrl: null,

    gtmBootstrapped: false,
    selectedAirportIata: null,

    modalActive: false,

    newsletterAirports: [],
    event: null,
    previousRoute: null,

    /**
     * Weather is stored on .data, with Object.freeze
     * Unable to stop pinia from making each property in first dimension of large object reactive (very slow)
     * weather.data is accessed as a getter
     */
    weatherData: {},
  }),
  persist: [
    {
      paths: ['selectedAirportIata'],
      appendPathsToKey: true,
      storage: localPersistedState.localStorage,
    },
    {
      paths: ['charterAirportNames'],
      appendPathsToKey: true,
      storage: localPersistedState.localStorageWithExpiration({
        expiration: expirations.longDuration,
      }),
    },
    {
      paths: ['rolfsCookie'],
      appendPathsToKey: true,
      storage: cookiesPersistedState.cookiesWithOptions({
        path: '/',
        maxAge: 60 * 60 * 24 * 365,
      }),
    },
  ],
  getters: {
    weather: (state) => state.weatherData?.data || {},
    shouldShowAgentCalls: (state) => !!state.showAgentCalls,
    showOfficeTools: (state) => state?.showOfficeTool,
    doShowTranslationTools: (state) => state?.showTranslationTool,

    showTranslationTools (state) {
      const localeStore = useLocaleStore()
      return state?.showTranslationTool && localeStore.translationToolsActive
    },

    getDestinationWeather: (state) => (destination: string) => {
      if (!state.weatherData?.data) {
        return null
      }

      return state.weatherData.data[destination]
    },

    getOgImage (state) {
      const { imagesConfig } = useConfigStore()
      return (
        state.pageBannerBackground?.image?.url ||
        state.pageBannerBackground?.picture_cloudinary ||
        imagesConfig.fallbackHeroId
      )
    },

    getCategoryById: state => (id: string | number) => {
      id = String(id)

      if (!state.tripTypesSidebar?.content) {
        return null
      }

      const content = Object.values(state.tripTypesSidebar.content) as Array<SectionCategory>

      for (const section of content) {
        const categories = section.categories
        if (!categories) {
          continue
        }

        for (const key in categories) {
          if (key.split('_')[1] === id) {
            return {
              category: {
                ...categories[key],
                url: categories[key].category_links?.[0]?.url,
              },
              sectionName: categories.name ?? null,
              fullKey: key,
            }
          }
        }
      }

      return null
    },
  },

  actions: {
    //old mutations
    SET_URL (url: string) {
      this.localUrl = url
    },

    SET_TRIP_TYPES (types: TripTypesSideBar) {
      this.tripTypesSidebar = types
    },

    SET_TOP_NAVBAR (navbar: Array<TopNavBarItem>) {
      this.topNavbar = navbar
    },

    SET_FOOTER_LINKS (links: string[]) {
      this.footerLinks = links
    },

    SET_PAGE_BANNER (data: any) {
      this.pageBannerTitle = data.title
      this.pageBannerTitleI18n = data.titleI18n
      this.pageBannerTitleDescription = data.titleDescription
      this.pageBannerTitleDescriptionI18n = data.titleDescriptionI18n
      this.pageBannerSubtitle = data.subtitle
      this.pageBannerSubtitleI18n = data.subtitleI18n
      this.pageBannerSubtitleColor = data.subtitleColor
      this.pageBannerSubtitleWeight = data.subtitleWeight
      this.pageBannerBackground = data.background
      this.pageBannerType = data.type
      this.pageMobileBannerType = data.mobileType
      this.pageBannerSize = data.size
      this.pageBannerLink = data.link
      this.pageBannerLinkButton = data.linkButton
      this.pageBannerLinkText = data.linkText
      this.pageBannerLinkTextI18n = data.linkTextI18n
      this.pageBannerStars = data.stars
      this.pageBannerPrice = data.price
      this.pageBannerDays = data.days
      this.pageBannerSpecial = data.special
      this.pageBannerShowSearchBar = data.showSearchBar
      this.bannerPriceWithoutDiscount = data.priceWithoutDiscount
      this.pagePaddingBottomPx = data.paddingBottomPx
      this.showNavigationArrow = data.showNavigationArrow
    },

    SET_PAGE_BANNER_TITLE (title: string) {
      this.pageBannerTitle = title
    },

    SET_PAGE_BANNER_STARS (stars: object) {
      this.pageBannerStars = stars
    },

    SET_PAGE_BANNER_SUBTITLE (subtitle: string) {
      this.pageBannerSubtitle = subtitle
    },

    SET_PAGE_BANNER_SUBTITLE_COLOR (color: string) {
      this.pageBannerSubtitleColor = color
    },

    SET_PAGE_BANNERTYPE (bannerType: string) {
      this.pageBannerType = bannerType
    },

    SET_PAGE_BANNER_BACKGROUND (data: PageBannerBackground) {
      this.pageBannerBackground = data
    },

    SET_PAGE_BANNER_LINK (link: string) {
      this.pageBannerLink = link
    },

    SET_PAGE_BANNER_LINK_BUTTON (link: string) {
      this.pageBannerLinkButton = link
    },

    SET_PAGE_BANNER_DAYS (days: number) {
      this.pageBannerDays = days
    },

    SET_PAGE_BANNER_PRICE (price: number) {
      this.pageBannerPrice = price
    },

    SET_PAGE_GRADIENT_MASK (gradientMask: boolean) {
      this.pageGradientMask = gradientMask
    },

    SET_PAGE_EXTRA_FOOTER (extraFooter: string | null) {
      this.pageExtraFooter = extraFooter
    },

    SET_START_CALENDAR (calendar: string[]) {
      this.startCalendar = calendar
    },

    SET_START_CATEGORIES (categories: string[]) {
      this.startCategories = categories
    },

    SET_CHARTER_AIRPORTS (airports: string[]) {
      this.charterAirports = airports
    },

    SET_CHARTER_AIRPORT_NAMES (names: object) {
      this.charterAirportNames = names
    },

    SET_CHARTER_DESTINATIONS (destinations: string[]) {
      this.charterDestinations = destinations
    },

    SET_SHOWOFFICETOOLS (officeTools: boolean) {
      this.showOfficeTool = officeTools
    },

    SET_SHOWAGENTTOOLS (agentTools: string | boolean) {
      this.showAgentTools = agentTools
    },

    SET_AGENTCOOKIE (cookie: AgentCookie | boolean) {
      this.agentCookie = cookie
    },

    SET_SHOWAGENTCALLS (showAgentCalls: boolean | null) {
      this.showAgentCalls = showAgentCalls
    },

    SET_ROLFSCOOKIE (cookie: string | null) {
      this.rolfsCookie = cookie
    },

    SET_ALERT (alert: object) {
      this.alert = alert
    },

    SET_GTM (gtmBootstrapped: boolean) {
      this.gtmBootstrapped = gtmBootstrapped
    },

    SET_NEWSLETTER_AIRPORTS (airports: string[]) {
      this.newsletterAirports = airports
    },

    SET_WEATHER (weatherData: WeatherData) {
      this.weatherData = Object.freeze(weatherData)
    },

    SET_EVENT (event: RolfsEvent[] | null) {
      this.event = event
    },

    SET_TRANSLATIONTOOLS (translationTool: boolean) {
      this.showTranslationTool = translationTool
      this.showTranslationTools
    },

    SET_CMSTOOLS (cmsTools: boolean) {
      this.cmsTools = cmsTools
    },

    SET_SELECTED_AIRPORT (airport: RootStoreState['selectedAirportIata']) {
      this.selectedAirportIata = airport
    },

    SET_MODAL_ACTIVE (active: boolean) {
      this.modalActive = active
    },

    //old actions
    async handleAgentCookie (agentCode?: string) {
      const { locale } = useLocaleStore()

      const agentCookie = useCookie<AgentCookie>('a')

      if (
        agentCode === AGENT_CODE_CLEAN_TYPE ||
        (agentCookie.value && '1752081984551' > agentCookie.value.currentDate) // Clean up old agent cookis TODO: remove
      ) {
        const deleteCookie = useCookie('a')
        deleteCookie.value = null

        this.SET_SHOWOFFICETOOLS(false)
        this.SET_SHOWAGENTTOOLS(false)
        this.SET_TRANSLATIONTOOLS(false)
        this.SET_CMSTOOLS(false)
        this.SET_AGENTCOOKIE(false)
        return
      }

      if (agentCode) {
        try {
          let cookie: AgentCookie

          if (agentCookie.value) {
            cookie = agentCookie.value
          } else {
            const { data: result } = await apiFetch<ResellerResponse>(
              `/${locale}/reseller/${agentCode}`
            )

            const { cookietime, show_officetools, show_agenttools, translations_tools, cms_tools } = result

            cookie = {
              code: agentCode,
              currentDate: Date.now().toString(),
              maxAge: cookietime,
              officetools: show_officetools,
              agenttools: show_agenttools ? agentCode : false,
              translationtools: translations_tools ?? false,
              cmstools: cms_tools || false,
            }
          }

          try {
            const cookieOpts = {
              maxAge: cookie.maxAge,
              path: '/',
            }

            const a = useCookie('a', {
              ...cookieOpts,
            })
            a.value = JSON.stringify({
              ...cookie,
            })
          } catch (e) {
          // Cannot set headers after they are sent to the client
          }
          this.SET_SHOWOFFICETOOLS(cookie.officetools)
          this.SET_SHOWAGENTTOOLS(cookie.agenttools)
          this.SET_TRANSLATIONTOOLS(cookie.translationtools)
          this.SET_CMSTOOLS(cookie.cmstools)
          this.SET_AGENTCOOKIE(cookie)
        } catch (error) {
          const { $sentryCaptureMessage } = useNuxtApp()

          $sentryCaptureMessage('Failed to get agentCookie', {
            level: 'info',
            contexts: {
              details: {
                id: agentCode,
              },
            },
            tags: {
              type: 'UX',
            },
          })
        }
        return
      }

      if (agentCookie.value) {
        this.SET_SHOWOFFICETOOLS(agentCookie.value.officetools)
        this.SET_SHOWAGENTTOOLS(agentCookie.value.agenttools)
        this.SET_TRANSLATIONTOOLS(agentCookie.value.translationtools)
        this.SET_CMSTOOLS(agentCookie.value.cmstools)
        this.SET_AGENTCOOKIE(agentCookie.value)
      }
    },

    async fetchAgentCall () {
      const { locale } = useLocaleStore()
      this.SET_SHOWAGENTCALLS(null)

      if (!this.showOfficeTools) {
        return
      }

      try {
        const { data: result } = await apiFetch<CurrentAgentCallsResponse>(
          `/${locale}/current-calls-for-agent`
        )

        const { showAgentCalls, error } = result

        if (error) {
          logFailedHttpRequest(error)
          return
        }
        this.SET_SHOWAGENTCALLS(showAgentCalls)
      } catch (error){
        logFailedHttpRequest(error)
      }
    },

    async fetchStartCalendar () {
      const { locale } = useLocaleStore()

      if (this.startCalendar.length > 0) {
        return
      }

      const { data: result, error } = await apiFetch<FetchStartCalendarResponse>(
        `/${locale}/calendar?hide_soldout=1`
      )

      const { departures } = result

      if (!departures && error) {
        logFailedHttpRequest(error)
        return
      }

      this.SET_START_CALENDAR(departures)
    },

    async fetchStartCategories () {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<string[]>(`/${locale}/categories-start`)

      this.SET_START_CATEGORIES(res)
    },

    async fetchCharterAirports () {
      const { locale } = useLocaleStore()

      if (this.charterAirports.length > 0) {
        return
      }

      const { data: res } = await apiFetch<string[]>(
        `/${locale}/charter/departure-airports`
      )

      this.SET_CHARTER_AIRPORTS(res)
    },

    async fetchCharterDestinations () {
      const { locale } = useLocaleStore()

      if (this.charterDestinations.length > 0) {
        return this.charterDestinations
      }

      const { data: result } = await apiFetch<string[]>(
        `/${locale}/charter/avalible-destinations`
      )

      this.SET_CHARTER_DESTINATIONS(result)

      return result
    },

    async fetchTripTypesSidebar (server = true) {
      const { locale } = useLocaleStore()
      try {
        const { data: response } = await apiFetch<TripTypesSideBar>(`/${locale}/sidebar`, {
          priority: server ? undefined : PRIORITY.LOW,
        })

        this.SET_TRIP_TYPES(response)
        return response
      } catch (error) {
        logFailedHttpRequest(error)
      }
    },

    async fetchTopNavbar () {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<Array<TopNavBarItem>>(`/${locale}/topnavbar`)

      const formatted = res.map((item) => ({
        ...item,
        label: item.label ? item.label.replace('\r', '\n') : item.label,
        submenu: Array.isArray(item.submenu)
          ? item.submenu.map((subItem) => ({
            ...subItem,
            label: subItem.label
              ? subItem.label.replace('\r', '\n')
              : subItem.label,
          }))
          : item.submenu,
      }))

      this.SET_TOP_NAVBAR(formatted)
    },

    async fetchFooter () {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<string[]>(`/${locale}/footer-links`)

      this.SET_FOOTER_LINKS(res)
    },

    async fetchAlert () {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<object>(`/${locale}/alert`)

      this.SET_ALERT(res)
    },

    async fetchHeroImage ({ page, onlyFetch }: { page: number; onlyFetch?: boolean }) {
      const { locale } = useLocaleStore()
      const { imagesConfig } = useConfigStore()

      const { data: res } = await apiFetch<PageBannerBackground>(`/${locale}/heroimage?page=${page}`, {
        throwOnFail: false,
      })

      res.picture_cloudinary = res.picture_cloudinary === 'hero/rome_sqg4ot'
        ? imagesConfig.fallbackHeroId
        : res.picture_cloudinary

      if (!onlyFetch) {
        this.SET_PAGE_BANNER_BACKGROUND(res)
      }

      return res
    },

    async fetchNewsletterLists () {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<string[]>(`/${locale}/newsletter-lists`)

      this.SET_NEWSLETTER_AIRPORTS(res)
      return res
    },

    async fetchWeather () {
      const { locale } = useLocaleStore()

      if (this.weatherData) {
        return
      }

      const { data: res } = await apiFetch<WeatherData>(`/${locale}/charter/weather`)

      this.SET_WEATHER(res)
    },

    async fetchSingleEvent ({ slug }: { slug: string }) {
      const { locale } = useLocaleStore()

      const { data: res } = await apiFetch<RolfsEvent[]>(`/${locale}/event/${slug}`)

      this.SET_EVENT(res)

      return res
    },

    async fetchUpcomingEvents () {
      const { locale } = useLocaleStore()

      const { data: result } = await apiFetch(`/${locale}/event-list`)
      return result
    },

    async registerForEvent ({
      id,
      email,
      name,
      pax,
    }: { id: string; email: string; name: string; pax: number }) {
      const { locale } = useLocaleStore()

      try {
        const { data: result } = await apiFetch(`/${locale}/event-register`, {
          method: 'POST',
          body: {
            event_id: id,
            email,
            name,
            number_pax: pax,
          },
        })

        return result
      } catch (error) {
        logFailedHttpRequest(error)
      }
    },

    async listAllTourleaders () {
      const { locale } = useLocaleStore()

      const { data: result } = await apiFetch(`/${locale}/tourleaders`)
      return result
    },

    async listAllInfoPages () {
      const { locale } = useLocaleStore()

      const { data: result } = await apiFetch(`/${locale}/info-pages`)
      return result
    },

    setSelectedAirport (airport: string) {
      this.SET_SELECTED_AIRPORT(airport)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRootStore, import.meta.hot))
}
